import { useMemo, useState, type ReactNode } from 'react';

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  Spinner
} from 'design-system';

export type DashboardSelectOption = {
  height?: number;
  info?: ReactNode;
  label: string;
  previewImage?: string;
  value: string;
};

export type DashboardSelectOptionGroup = {
  label: string;
  options: DashboardSelectOption[];
};

type DashboardGroupedSelectProps = {
  disabled?: boolean;
  isLoading?: boolean;
  label: string;
  options: (DashboardSelectOption | DashboardSelectOptionGroup)[];
  placeholder: string;
  showPreview?: boolean;
  value: string | null;
  onChange: (option: DashboardSelectOption) => void;
};

type ComboboxOptionGroup = {
  items: DashboardSelectOption[];
  value: string;
};

const isOptionGroup = (
  option: DashboardSelectOption | DashboardSelectOptionGroup
): option is DashboardSelectOptionGroup => 'options' in option;

export const DashboardGroupedSelect = ({
  disabled,
  isLoading,
  label,
  options,
  placeholder,
  showPreview = false,
  value,
  onChange
}: DashboardGroupedSelectProps) => {
  const id = `dashboard-${label.replaceAll(' ', '-').toLowerCase()}`;
  const [highlightedOption, setHighlightedOption] = useState<DashboardSelectOption>();

  const optionGroups = useMemo<ComboboxOptionGroup[]>(() => {
    const directOptions = options.filter(
      (option): option is DashboardSelectOption => !isOptionGroup(option)
    );
    const groups = options.filter(isOptionGroup).map(group => ({
      items: group.options,
      value: group.label
    }));

    return [
      ...(directOptions.length > 0
        ? [
            {
              items: directOptions,
              value: ''
            }
          ]
        : []),
      ...groups
    ];
  }, [options]);

  const flatOptions = useMemo(
    () => optionGroups.flatMap(optionGroup => optionGroup.items),
    [optionGroups]
  );
  const selectedOption = flatOptions.find(option => option.value === value) ?? null;
  const preview =
    showPreview && highlightedOption?.previewImage ? (
      <img
        alt={`${highlightedOption.label} preview`}
        className='mx-auto w-full object-contain'
        src={highlightedOption.previewImage}
        style={{ maxHeight: highlightedOption.height }}
      />
    ) : undefined;

  return (
    <div className='grid gap-2'>
      <label
        className='text-sm font-semibold'
        htmlFor={id}
      >
        {label}
      </label>
      <div className='flex items-center gap-2'>
        <Combobox
          disabled={disabled || isLoading}
          isItemEqualToValue={(option, selected) => option.value === selected.value}
          items={optionGroups}
          value={selectedOption}
          onItemHighlighted={option => {
            setHighlightedOption(option);
          }}
          onOpenChange={open => {
            if (!open) setHighlightedOption(undefined);
          }}
          onValueChange={option => {
            if (option) onChange(option);
          }}
        >
          <ComboboxInput
            aria-label={label}
            className='w-full'
            disabled={disabled || isLoading}
            id={id}
            placeholder={placeholder}
          >
            <ComboboxContent preview={preview}>
              <ComboboxEmpty>No options found.</ComboboxEmpty>
              <ComboboxList>
                {(optionGroup: ComboboxOptionGroup, groupIndex: number) => (
                  <ComboboxGroup
                    key={`${optionGroup.value}-${groupIndex}`}
                    items={optionGroup.items}
                  >
                    {optionGroup.value && <ComboboxLabel>{optionGroup.value}</ComboboxLabel>}
                    <ComboboxCollection>
                      {(option: DashboardSelectOption) => (
                        <ComboboxItem
                          key={option.value}
                          value={option}
                        >
                          <span className='min-w-0 flex-1 truncate'>{option.label}</span>
                          {option.info}
                        </ComboboxItem>
                      )}
                    </ComboboxCollection>
                  </ComboboxGroup>
                )}
              </ComboboxList>
            </ComboboxContent>
          </ComboboxInput>
        </Combobox>
        {isLoading && (
          <span className='flex items-center gap-1 text-xs text-muted-foreground'>
            <Spinner className='size-4' />
            {label} loading
          </span>
        )}
      </div>
    </div>
  );
};
